/* *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 *
 * Copyright 2025 - Koninklijk Nederlands Meteorologisch Instituut (KNMI)
 * Copyright 2025 - Finnish Meteorological Institute (FMI)
 * Copyright 2025 - The Norwegian Meteorological Institute (MET Norway)
 * */

import { Menu, ListSubheader, Typography, MenuItem } from '@mui/material';
import React from 'react';
import { CustomToggleButton } from '@opengeoweb/shared';
import { getDiagramName } from './drawUtils';
import type { SoundingDiagramType } from '../../api/edr-api/types';
import { soundingDiagrams } from '../../api/edr-api/types';
import { useSoundingsTranslation } from '../../utils/i18n';

interface DiagramTypeButtonProps {
  diagramType: SoundingDiagramType;
  setDiagramType: (diagramType: SoundingDiagramType) => void;
}

export const DiagramTypeButton = ({
  diagramType,
  setDiagramType,
}: DiagramTypeButtonProps): React.ReactElement => {
  const { t } = useSoundingsTranslation();
  const [anchorEl, setAnchorEl] = React.useState<
    HTMLInputElement | Element | null
  >(null);

  const isOpen = Boolean(anchorEl);
  const handleClick = (event: React.MouseEvent): void => {
    setAnchorEl(anchorEl ? null : event.currentTarget);
  };

  return (
    <CustomToggleButton
      data-testid="soundings-diagramtype-options-toolbutton"
      disableRipple
      onClick={handleClick}
      role="button"
      selected={isOpen}
      size="small"
      sx={{
        height: '24px !important',
        width: '75px',
      }}
      variant="tool"
    >
      {getDiagramName(t, diagramType)}
      <Menu
        anchorEl={anchorEl}
        anchorOrigin={{
          vertical: 21,
          horizontal: 6,
        }}
        autoFocus={false}
        open={isOpen}
        slotProps={{
          backdrop: {
            style: {
              opacity: 0,
              transition: 'none',
            },
          },
        }}
        sx={{
          '& .MuiMenu-list': {
            padding: 0,
          },
          '& .MuiMenuItem-root': {
            fontSize: 16,
          },
        }}
      >
        <ListSubheader
          sx={{
            paddingTop: '8px',
          }}
        >
          <Typography
            sx={{
              letterSpacing: '0.4px',
              fontSize: 12,
            }}
          >
            {t('options')}
          </Typography>
        </ListSubheader>
        {soundingDiagrams.map((id) => (
          <MenuItem
            key={id}
            onClick={() => {
              setDiagramType(id as SoundingDiagramType);
            }}
            selected={diagramType === id}
          >
            {getDiagramName(t, id)}
          </MenuItem>
        ))}
      </Menu>
    </CustomToggleButton>
  );
};
